import type {
  HoldingUnitCapabilities,
  HoldingUnitContainerKind,
  HoldingUnitIdentityMode,
  HoldingUnitProductKind,
  HoldingUnitProfile,
  HoldingUnitType,
  TareProfileMode,
  UnitProfileCategory,
  UnitProfileFixedIdPolicy,
  UnitProfileTemplateDto,
  UnitProfileTemplateKey,
} from '@crv/contracts';

const capabilityNames = [
  'canReceiveIntake',
  'canReceiveTransfer',
  'canTransferOut',
  'canAdjust',
  'canReady',
  'canShip',
  'canRetire',
  'canBuildProduct',
  'canHoldLeftover',
  'canReopen',
  'canDisassemble',
] as const satisfies readonly (keyof HoldingUnitCapabilities)[];

export type UnitProfileCapabilityName = (typeof capabilityNames)[number];

export const UNIT_PROFILE_CAPABILITY_NAMES = [
  ...capabilityNames,
] as readonly UnitProfileCapabilityName[];

const noCapabilities: HoldingUnitCapabilities = {
  canReceiveIntake: false,
  canReceiveTransfer: false,
  canTransferOut: false,
  canAdjust: false,
  canReady: false,
  canShip: false,
  canRetire: false,
  canBuildProduct: false,
  canHoldLeftover: false,
  canReopen: false,
  canDisassemble: false,
};

function capabilities(enabled: readonly UnitProfileCapabilityName[]): HoldingUnitCapabilities {
  const result: HoldingUnitCapabilities = { ...noCapabilities };
  for (const name of enabled) {
    result[name] = true;
  }
  return result;
}

const sourceCapabilities = capabilities([
  'canReceiveIntake',
  'canReceiveTransfer',
  'canTransferOut',
  'canAdjust',
  'canReady',
  'canShip',
  'canRetire',
  'canReopen',
]);

const stagingCapabilities = capabilities([
  'canReceiveTransfer',
  'canTransferOut',
  'canAdjust',
  'canRetire',
  'canBuildProduct',
  'canHoldLeftover',
]);

const productCapabilities = capabilities([
  'canReceiveIntake',
  'canReady',
  'canShip',
  'canRetire',
  'canReopen',
  'canDisassemble',
]);

const leftoverCapabilities = capabilities([
  'canReceiveTransfer',
  'canTransferOut',
  'canAdjust',
  'canReady',
  'canShip',
  'canRetire',
  'canReopen',
]);

const allTareModes: TareProfileMode[] = [
  'none',
  'fixed_default',
  'manual_required',
  'fixed_asset_master',
];
const nonFixedAssetTareModes: TareProfileMode[] = ['none', 'fixed_default', 'manual_required'];

export interface UnitProfileTemplateDefinition extends UnitProfileTemplateDto {
  readonly maxCapabilities: HoldingUnitCapabilities;
  readonly defaultCapabilityPolicy: HoldingUnitCapabilities;
  readonly allowedTareModes: TareProfileMode[];
}

interface TemplateConfig {
  templateKey: UnitProfileTemplateKey;
  profileCategory: UnitProfileCategory;
  unitType: HoldingUnitType;
  identityMode: HoldingUnitIdentityMode;
  containerKind: HoldingUnitContainerKind;
  productKind: HoldingUnitProductKind;
  fixedIdPolicy: UnitProfileFixedIdPolicy;
  displayName: string;
  description: string;
  maxCapabilities: HoldingUnitCapabilities;
  allowedTareModes: TareProfileMode[];
}

function template(config: TemplateConfig): UnitProfileTemplateDefinition {
  return {
    ...config,
    defaultCapabilityPolicy: { ...config.maxCapabilities },
    maxCapabilities: { ...config.maxCapabilities },
    allowedTareModes: [...config.allowedTareModes],
  };
}

export const UNIT_PROFILE_TEMPLATE_DEFINITIONS = [
  template({
    templateKey: 'fixed_id_source_heavy_container',
    profileCategory: 'source',
    unitType: 'source',
    identityMode: 'fixed_asset_cycle',
    containerKind: 'heavy_container',
    productKind: 'none',
    fixedIdPolicy: 'fixed_asset_required',
    displayName: 'Fixed-ID source container',
    description: 'Fixed asset heavy container used for repeated source intake cycles.',
    maxCapabilities: sourceCapabilities,
    allowedTareModes: allTareModes,
  }),
  template({
    templateKey: 'source_loose',
    profileCategory: 'source',
    unitType: 'source',
    identityMode: 'generic',
    containerKind: 'loose',
    productKind: 'none',
    fixedIdPolicy: 'none',
    displayName: 'Loose source',
    description: 'Generic loose source material received into inventory.',
    maxCapabilities: sourceCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'source_bag',
    profileCategory: 'source',
    unitType: 'source',
    identityMode: 'generic',
    containerKind: 'bag',
    productKind: 'none',
    fixedIdPolicy: 'none',
    displayName: 'Bag source',
    description: 'Generic bag source material received into inventory.',
    maxCapabilities: sourceCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'source_heavy_container',
    profileCategory: 'source',
    unitType: 'source',
    identityMode: 'generic',
    containerKind: 'heavy_container',
    productKind: 'none',
    fixedIdPolicy: 'none',
    displayName: 'Heavy-container source',
    description: 'Generic heavy-container source material received into inventory.',
    maxCapabilities: sourceCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'staging_container',
    profileCategory: 'staging',
    unitType: 'staging',
    identityMode: 'generic',
    containerKind: 'staging_container',
    productKind: 'none',
    fixedIdPolicy: 'none',
    displayName: 'Staging container',
    description: 'Intermediate staging container for build-product workflows.',
    maxCapabilities: stagingCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'product_none',
    profileCategory: 'product',
    unitType: 'product',
    identityMode: 'generic',
    containerKind: 'none',
    productKind: 'none',
    fixedIdPolicy: 'none',
    displayName: 'Product',
    description: 'Generic product unit without a specialized product kind.',
    maxCapabilities: productCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'product_bale',
    profileCategory: 'product',
    unitType: 'product',
    identityMode: 'generic',
    containerKind: 'none',
    productKind: 'bale',
    fixedIdPolicy: 'none',
    displayName: 'Baled product',
    description: 'Baled product output from product build or inbound posting.',
    maxCapabilities: productCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'product_paca',
    profileCategory: 'product',
    unitType: 'product',
    identityMode: 'generic',
    containerKind: 'none',
    productKind: 'paca',
    fixedIdPolicy: 'none',
    displayName: 'Paca product',
    description: 'Paca product output from product build or inbound posting.',
    maxCapabilities: productCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'leftover_loose',
    profileCategory: 'leftover',
    unitType: 'leftover',
    identityMode: 'generic',
    containerKind: 'loose',
    productKind: 'none',
    fixedIdPolicy: 'none',
    displayName: 'Loose leftover',
    description: 'Loose leftover material produced by product build.',
    maxCapabilities: leftoverCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'leftover_bag',
    profileCategory: 'leftover',
    unitType: 'leftover',
    identityMode: 'generic',
    containerKind: 'bag',
    productKind: 'none',
    fixedIdPolicy: 'none',
    displayName: 'Bag leftover',
    description: 'Bagged leftover material produced by product build.',
    maxCapabilities: leftoverCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
  template({
    templateKey: 'leftover_heavy_container',
    profileCategory: 'leftover',
    unitType: 'leftover',
    identityMode: 'generic',
    containerKind: 'heavy_container',
    productKind: 'none',
    fixedIdPolicy: 'none',
    displayName: 'Heavy-container leftover',
    description: 'Heavy-container leftover material produced by product build.',
    maxCapabilities: leftoverCapabilities,
    allowedTareModes: nonFixedAssetTareModes,
  }),
] as const satisfies readonly UnitProfileTemplateDefinition[];

export function listUnitProfileTemplateDefinitions(): UnitProfileTemplateDefinition[] {
  return UNIT_PROFILE_TEMPLATE_DEFINITIONS.map(cloneTemplate);
}

export function getUnitProfileTemplateDefinition(
  templateKey: string,
): UnitProfileTemplateDefinition | null {
  const definition = UNIT_PROFILE_TEMPLATE_DEFINITIONS.find(
    (candidate) => candidate.templateKey === templateKey,
  );
  return definition ? cloneTemplate(definition) : null;
}

export function findUnitProfileTemplateForTuple(
  profile: HoldingUnitProfile,
): UnitProfileTemplateDefinition | null {
  const definition = UNIT_PROFILE_TEMPLATE_DEFINITIONS.find(
    (candidate) =>
      candidate.unitType === profile.unitType &&
      candidate.identityMode === profile.identityMode &&
      candidate.containerKind === profile.containerKind &&
      candidate.productKind === profile.productKind,
  );
  return definition ? cloneTemplate(definition) : null;
}

function cloneTemplate(definition: UnitProfileTemplateDefinition): UnitProfileTemplateDefinition {
  return {
    ...definition,
    maxCapabilities: { ...definition.maxCapabilities },
    defaultCapabilityPolicy: { ...definition.defaultCapabilityPolicy },
    allowedTareModes: [...definition.allowedTareModes],
  };
}
